import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import PlanningTable from './components/PlanningTable';
import PlanningForm from './components/PlanningForm';
import useTrainingPlanningsData from './hooks/useTrainingPlanningsData';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useLmsPermission } from '../hooks/useLmsPermission';
import { LMS } from '../../../constants/permissionCodes';

/**
 * Eğitim Planlamaları sayfası — daha önce Eğitim Kataloğu detayının bir sekmesiydi
 * (`training-catalog/components/PlanningTab.jsx`), artık bağımsız bir sayfa. Giriş noktası
 * `my-trainings/index.jsx`'teki "Eğitim Dönemleri ve Katalog" kartındaki "Planlamalar" satırı
 * (eskiden "Eğitimler" — henüz içeriği olmayan bir yer tutucuydu, bu sayfa onun yerine geçti).
 * Bkz. docs/LMS-Egitim-Katalogu-ve-Egitmenler-API 2.md §8-9.
 */
const TrainingPlanningPage = () => {
    const { th } = useLanguage();
    const { can } = useLmsPermission();
    const { planningsApi, isLoading, isSaving, isDeleting, savePlanning, deletePlanning } = useTrainingPlanningsData();
    const [selectedPlanning, setSelectedPlanning] = useState(null);
    const [formVisible, setFormVisible] = useState(false);

    const plannings = planningsApi?.items || [];

    const handleCreate = () => {
        setSelectedPlanning(null);
        setFormVisible(true);
    };

    const handleEdit = (row) => {
        setSelectedPlanning(row);
        setFormVisible(true);
    };

    const handleHideForm = () => {
        setFormVisible(false);
        setSelectedPlanning(null);
    };

    const handleSubmit = async (payload, pendingInstructorUserIds, newsRequest) => {
        const { success, reopenPlanning } = await savePlanning(payload, pendingInstructorUserIds, newsRequest);
        if (!success) return;
        if (reopenPlanning) {
            setSelectedPlanning(reopenPlanning);
            return;
        }
        handleHideForm();
    };

    const handleDelete = (id) => {
        if (!id) return;
        confirmDialog({
            message: th('trainingPlanning.confirmDeleteMessage'),
            header: th('common.delete'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                const success = await deletePlanning(id);
                if (success) handleHideForm();
            }
        });
    };

    return (
        <div className="card">
            <ConfirmDialog />

            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('trainingPlanning.pageTitle')}</h1>
                {can(LMS.TRAINING_CREATE) && <Button label={th('trainingPlanning.addButton')} icon="pi pi-plus" onClick={handleCreate} />}
            </div>

            <PlanningTable plannings={plannings} loading={isLoading} onEdit={handleEdit} />

            {formVisible && (
                <PlanningForm
                    visible={formVisible}
                    onHide={handleHideForm}
                    onSubmit={handleSubmit}
                    initialData={selectedPlanning}
                    loading={isSaving || isDeleting}
                    handleDelete={can(LMS.TRAINING_DELETE) ? handleDelete : undefined}
                    readOnly={!!selectedPlanning && !can(LMS.TRAINING_UPDATE)}
                    instructorsReadOnly={!can([LMS.TRAINING_CREATE, LMS.TRAINING_UPDATE])}
                />
            )}
        </div>
    );
};

export default TrainingPlanningPage;
